import { useState } from 'react'
import { useParams } from 'react-router'
import toNumber from 'lodash/toNumber'
import sumBy from 'lodash/sumBy'
import toString from 'lodash/toString'
import { ChevronLeft, ChevronRight, Download, Pencil, PlusIcon } from 'lucide-react'
import { PERMISSION_ACTION_ADD, PERMISSION_ENTITY_ORGANIZATION } from '@klear/constants'
import type { TraderDocument } from '@klear/document/trader-document'
import type { DocumentStatus } from '@klear/schema/documents'
import { DOCUMENT_TYPE_LABEL, DOCUMENT_TYPE_UNCLASSIFIED, type DocumentType } from '@klear/constants/document-types'
import { DocumentTypeFilter } from '@klear/ui/components/document-toolbar'
import { DocumentViewButton } from '@klear/ui/components/document-view-button'
import { NOT_PREVIEWABLE_MESSAGE } from '@klear/upload/content-type'
import { Badge } from '@klear/ui/components/badge'
import { Button } from '@klear/ui/components/button'
import { useConfirmDialog } from '@klear/ui/components/alert-dialog'
import ImgPreviewModal from '@klear/ui/components/img-preview-modal'
import { Input } from '@klear/ui/components/input'
import PdfPreviewModal from '@klear/ui/components/pdf-preview-modal'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@klear/ui/components/table'
import { Tabs, TabsList, TabsTrigger } from '@klear/ui/components/tabs'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { useDebouncedValue } from '@klear/ui/hooks/use-debounced-value'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'
import { createToastConfig, useMutationWithToast } from '@klear/ui/hooks/use-mutations-with-toast'
import { useFileDownload } from '@klear/ui/lib/download-file'
import { trpc } from '@klear/ui/lib/trpc'
import { cn } from '@klear/ui/lib/utils'
import { buildDocumentFileUrl, withAttachmentDisposition } from '@klear/upload/file-url'
import { DocumentReferences } from '@/app/document/document-references'
import { EditDocumentDialog } from '@/app/document/edit-document-dialog'
import { UploadDocumentDialog } from '@/app/document/upload-document-dialog'

const PAGE_SIZE = 50
const PROCESSING_POLL_MS = 5000

const TABS = [
  { id: 'all', label: 'All', statuses: [] },
  { id: 'reviewing', label: 'Reviewing', statuses: ['processing', 'needs_review'] },
  { id: 'filed', label: 'Filed', statuses: ['filed'] }
] as const satisfies ReadonlyArray<{ id: string; label: string; statuses: readonly DocumentStatus[] }>

type TabId = (typeof TABS)[number]['id']

const STATUS_LOOK: Record<DocumentStatus, { label: string; tone: string }> = {
  processing: { label: 'Reviewing', tone: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300' },
  needs_review: { label: 'Reviewing', tone: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300' },
  filed: { label: 'Filed', tone: 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300' },
  dismissed: { label: 'Dismissed', tone: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300' }
}

const typeLabel = (document: TraderDocument) =>
  document.type === DOCUMENT_TYPE_UNCLASSIFIED ? '—' : DOCUMENT_TYPE_LABEL[document.type]

const formatted = (document: TraderDocument) =>
  new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(document.arrivedAt.epochMs)

function FileActions({ document, traderId }: { document: TraderDocument; traderId: string }) {
  const [previewOpen, setPreviewOpen] = useState(false)
  const { startDownload, isDownloading } = useFileDownload()
  const url = buildDocumentFileUrl(toString(document.id), traderId)
  const attachmentUrl = withAttachmentDisposition(url)
  return (
    <div className="flex items-center justify-end gap-0.5">
      <DocumentViewButton
        name={document.name}
        unavailableReason={document.isPdf || document.isImage ? null : NOT_PREVIEWABLE_MESSAGE}
        onView={() => setPreviewOpen(true)}
      />
      <Button
        variant="ghost"
        className="h-8 w-8 p-0"
        onClick={() => startDownload(attachmentUrl, document.name)}
        disabled={isDownloading}
        title="Download"
      >
        <Download className="h-4 w-4" />
        <span className="sr-only">Download {document.name}</span>
      </Button>
      {document.isPdf && (
        <PdfPreviewModal
          title={document.name}
          pdfUrl={url}
          downloadUrl={attachmentUrl}
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          downloadable
        />
      )}
      {document.isImage && (
        <ImgPreviewModal
          fileUrl={url}
          downloadUrl={attachmentUrl}
          fileName={document.name}
          open={previewOpen}
          onOpenChange={setPreviewOpen}
        />
      )}
    </div>
  )
}

export default function DocumentsPage() {
  useDocumentTitle('Documents')
  const { user } = useAuth()
  const { traderId } = useParams()
  const [uploadOpen, setUploadOpen] = useState(false)
  const [editing, setEditing] = useState<{
    document: TraderDocument
    canDelete: boolean
    deleteBlockedReason: string | null
  } | null>(null)
  const [tab, setTab] = useState<TabId>('all')
  const [search, setSearch] = useState('')
  const [types, setTypes] = useState<DocumentType[]>([])
  const [page, setPage] = useState(0)
  const debouncedSearch = useDebouncedValue(search)
  const { confirm, ConfirmDialog } = useConfirmDialog()
  const removeDocument = useMutationWithToast(
    trpc.documents.update.useMutation,
    createToastConfig('Document', 'delete'),
    {
      invalidateQueries: ['documents.list']
    }
  )

  const statuses = TABS.find((candidate) => candidate.id === tab)?.statuses ?? []
  const listQuery = trpc.documents.list.useQuery(
    {
      ...(traderId !== undefined && { traderId: toNumber(traderId) }),
      asTrader: true,
      ...(debouncedSearch.trim() !== '' && { search: debouncedSearch.trim() }),
      ...(statuses.length > 0 && { statuses: [...statuses] }),
      ...(types.length > 0 && { types }),
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE
    },
    {
      enabled: traderId !== undefined,
      placeholderData: (previous) => previous,
      refetchInterval: (query) =>
        query.state.data?.rows.some(({ document }) => document.status === 'processing') === true
          ? PROCESSING_POLL_MS
          : false,
      refetchOnWindowFocus: true
    }
  )
  if (!traderId) return null

  const canUpload = user.hasPermission(PERMISSION_ENTITY_ORGANIZATION, PERMISSION_ACTION_ADD)
  const rows = listQuery.data?.rows ?? []
  const counts = listQuery.data?.statusCounts
  const total = listQuery.data?.page.totalRows ?? 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const remove = (document: TraderDocument) =>
    confirm({
      title: 'Delete Document',
      description: `Are you sure you want to delete "${document.name}"? This action cannot be undone.`,
      onConfirm: async () => {
        await removeDocument.mutateAsync({ id: document.id, data: { removeFrom: [{ kind: 'documents_page' }] } })
        setEditing(null)
      }
    })

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-semibold">Documents</h1>
        {canUpload && (
          <Button variant="outline" onClick={() => setUploadOpen(true)}>
            <PlusIcon className="mr-2 h-4 w-4" />
            Upload Document
          </Button>
        )}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Tabs
          value={tab}
          activationMode="manual"
          onValueChange={(value) => {
            const picked = TABS.find((candidate) => candidate.id === value)
            if (picked === undefined) return
            setTab(picked.id)
            setPage(0)
          }}
        >
          <TabsList aria-label="Status" className="h-auto w-fit">
            {TABS.map((candidate) => (
              <TabsTrigger
                key={candidate.id}
                value={candidate.id}
                className="h-7 gap-2 data-[state=active]:shadow-none"
              >
                {candidate.label}
                {counts !== undefined && (
                  <span className="inline-grid h-5 min-w-5 place-items-center rounded-full bg-border px-1.5 text-xs tabular-nums">
                    {candidate.id === 'all'
                      ? counts.all
                      : sumBy(candidate.statuses, (status: DocumentStatus) => counts[status] ?? 0)}
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-2">
          <DocumentTypeFilter
            selected={types}
            onChange={(picked) => {
              setTypes(picked)
              setPage(0)
            }}
          />
          <Input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value)
              setPage(0)
            }}
            placeholder="Search name or notes"
            className="h-9 w-64"
          />
        </div>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="hidden md:table-cell">Type</TableHead>
              <TableHead>Filed to</TableHead>
              <TableHead className="hidden md:table-cell">Added</TableHead>
              <TableHead>
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {listQuery.isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center">
                  Loading…
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center">
                  No documents found.
                </TableCell>
              </TableRow>
            ) : (
              rows.map(({ document, allowedActions, removeBlockedReason }) => {
                const look = STATUS_LOOK[document.status]
                const canEdit = allowedActions.includes('update')
                const canDelete = allowedActions.includes('remove')
                return (
                  <TableRow key={document.id}>
                    <TableCell className="max-w-[220px] font-medium sm:max-w-[320px]">
                      <div className="group flex items-center gap-1">
                        <div className="truncate" title={document.name}>
                          {document.name}
                        </div>
                        {canEdit && (
                          <Button
                            variant="ghost"
                            size="xs"
                            aria-label={`Edit ${document.name}`}
                            onClick={() =>
                              setEditing({ document, canDelete, deleteBlockedReason: removeBlockedReason })
                            }
                            className="-my-0.5 shrink-0 opacity-40 group-hover:opacity-100 focus-visible:opacity-100"
                          >
                            <Pencil className="h-3.5 w-3.5" aria-hidden />
                          </Button>
                        )}
                      </div>
                      {document.notes !== '' && (
                        <div
                          className="text-muted-foreground mt-0.5 truncate text-xs font-normal"
                          title={document.notes}
                        >
                          {document.notes}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={cn('whitespace-nowrap font-medium', look.tone)}>{look.label}</Badge>
                    </TableCell>
                    <TableCell className="hidden whitespace-nowrap md:table-cell">{typeLabel(document)}</TableCell>
                    <TableCell>
                      <DocumentReferences traderId={traderId} references={document.references} />
                    </TableCell>
                    <TableCell className="hidden whitespace-nowrap md:table-cell">{formatted(document)}</TableCell>
                    <TableCell className="py-1">
                      <FileActions document={document} traderId={traderId} />
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">
          {total} document{total !== 1 ? 's' : ''}
        </span>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 0}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span>
            Page {page + 1} of {totalPages}
          </span>
          <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page + 1 >= totalPages}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
      {canUpload && (
        <UploadDocumentDialog open={uploadOpen} onOpenChange={setUploadOpen} traderId={toNumber(traderId)} />
      )}
      {editing !== null && (
        <EditDocumentDialog
          document={editing.document}
          deleteBlockedReason={editing.deleteBlockedReason}
          onClose={() => setEditing(null)}
          {...(editing.canDelete && { onDelete: () => remove(editing.document) })}
        />
      )}
      {ConfirmDialog}
    </div>
  )
}
